import React, { useState, useEffect, useRef } from 'react'
import {
  Flex,
  CursorPointer,
  AddButton,
  Separator,
  SelectImageContainer,
  Button,
  ImageUpload,
  H1,
} from '../../styling/GlobalStyles'
import { Input } from '../Input/Input'
import { AddCircle } from '../../assets/icons/AddCircle'
import { CloseIcon } from '../../assets/icons/CloseIcon'
import { useDispatch } from 'react-redux'
import {
  addCategory,
  deleteCategory,
  updateCategory,
} from '../../features/categories/categoriesSlice'
import { getBase64 } from '../../utils/helpers'
import { setConfirmModal } from 'features/global/globalSlice'
import { AppDispatch } from 'store'
import { ICategotyModal } from 'constants/globalTypes'
import {
  addMachineryCategory,
  deleteMachineryCategory,
  updateMachineryCategory,
} from 'features/categories/machineryCategories'
import useFocusFirstInput from 'utils/useFocusFirstInput'
import { BodyText } from 'styling/Typography'
import {
  getMachineCategoryIcon,
  MACHINE_CATEGORY_ICON_OPTIONS,
} from 'pages/Machinery/machineCategoryIcons'
import { ShiftIconSelectBox } from 'pages/Shifts/Shifts.styled'
import { tokens } from 'styling/tokens'

export const CategoryModal = ({
  close,
  edit,
  text,
  id,
  image,
  iconName,
  machinery,
  fields,
  setParams,
  imageFile,
}: ICategotyModal) => {
  const dispatch = useDispatch<AppDispatch>()
  const [name, setName] = useState(text)
  const [imageParsed, setImageParsed] = useState(image)
  const [selectedIconName, setSelectedIconName] = useState(iconName || 'bulldozer')
  const modalRef = useRef<HTMLDivElement>(null)

  useFocusFirstInput()

  useEffect(() => {
    if (imageFile) {
      getBase64(imageFile).then((res: string) => setImageParsed(res))
    }

    const keyDownHandler = (event: KeyboardEvent) => {
      if (event.keyCode === 27) {
        event.preventDefault()
        close({ open: false })
      }
    }
    const outSideClick = (e) => {
      if (!modalRef.current.contains(e.target)) {
        close({ open: false })
      }
    }

    document.addEventListener('keydown', keyDownHandler)
    document.addEventListener('mousedown', outSideClick)

    return () => {
      document.removeEventListener('keydown', keyDownHandler)
      document.removeEventListener('mousedown', outSideClick)
    }
  }, [])

  const handleDelete = () => {
    dispatch(
      setConfirmModal({
        isOpen: true,
        onSave: () => {
          dispatch(machinery ? deleteMachineryCategory(Number(id)) : deleteCategory(Number(id)))
          dispatch(setConfirmModal({ isOpen: false }))
        },
        word: 'delete category',
        setParams: setParams,
      }),
    )
    close({ open: false })
  }

  const handleSubmit = async () => {
    let resp: any
    if (machinery) {
      const clientId = 1
      if (edit) {
        resp = await dispatch(
          updateMachineryCategory({
            name,
            iconName: selectedIconName,
            clientId,
            id,
            fields: fields || [],
          }),
        )
      } else {
        resp = await dispatch(
          addMachineryCategory({ name, iconName: selectedIconName, clientId }),
        )
      }
    } else {
      if (edit) {
        const obj = { name, image: imageParsed, id, fields, localImage: null }
        if (obj.image && obj.image.startsWith('http')) {
          delete obj.image
          obj.localImage = imageParsed
        }
        resp = await dispatch(updateCategory(obj))
      } else {
        resp = await dispatch(addCategory({ name, image: imageParsed }))
      }
    }
    if (resp.meta.requestStatus === 'fulfilled') {
      close({ open: false })
    }
  }

  return (
    <div>
      <Flex column ref={modalRef}>
        <Flex between width='100%'>
          <H1>{edit ? 'Edit ' + text : 'Add Category'}</H1>
          <CursorPointer onClick={() => close({ open: false })}>
            <CloseIcon />
          </CursorPointer>
        </Flex>
        <Separator />
        <Flex gap='1rem' between width='100%' height={machinery ? 'auto' : '10rem'} column={!!machinery}>
          {!machinery && (!imageParsed ? (
            <ImageUpload>
              <AddButton
                width='180px'
                height='130px'
                onClick={(e: any) => {
                  e.target.parentElement.children[1].click()
                }}
              >
                <AddCircle /> <BodyText weight='bold'>Add Image</BodyText>
              </AddButton>
              <input
                type='file'
                name='imageupload'
                accept='image/*'
                onChange={(e) => {
                  getBase64(e.target.files[0]).then((res: string) => setImageParsed(res))
                }}
              />
            </ImageUpload>
          ) : (
            <ImageUpload>
              <SelectImageContainer
                style={{ cursor: 'pointer' }}
                onClick={(e: any) => {
                  e.target.parentElement.parentElement.children[1].click()
                }}
              >
                <img src={imageParsed} alt='' />
              </SelectImageContainer>
              <input
                type='file'
                name='imageupload'
                accept='image/*'
                onChange={(e) => {
                  getBase64(e.target.files[0]).then((res: string) => setImageParsed(res))
                }}
              />
            </ImageUpload>
          ))}
          <Flex column between ml={machinery ? '0' : '10px'} width={machinery ? '100%' : '60%'} gap='1rem'>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              labelText='Title'
              type='text'
              placeholder='Enter category title'
              nomarg
            />

            {machinery && (
              <>
                <BodyText weight='bold'>Icon</BodyText>
                <Flex width='100%' gap='0.75rem' wrap>
                  {MACHINE_CATEGORY_ICON_OPTIONS.map((option) => (
                    <ShiftIconSelectBox
                      key={option.iconName}
                      title={option.label}
                      active={selectedIconName === option.iconName}
                      onClick={() => setSelectedIconName(option.iconName)}
                    >
                      {getMachineCategoryIcon(option.iconName, tokens.accent.base, 28)}
                    </ShiftIconSelectBox>
                  ))}
                </Flex>
              </>
            )}

            {edit ? (
              <Flex>
                <Flex width='100%' between>
                  <Button pink width='135' onClick={handleDelete}>
                    Delete
                  </Button>
                  <Button
                    orange
                    width='135'
                    disabled={!name || name.length < 1}
                    onClick={handleSubmit}
                  >
                    Save
                  </Button>
                </Flex>
              </Flex>
            ) : (
              <Flex width='100%' end='true'>
                <Button disabled={!name || name.length < 1} orange onClick={handleSubmit}>
                  Save
                </Button>
              </Flex>
            )}
          </Flex>
        </Flex>
      </Flex>
    </div>
  )
}
